/**
 * The pure half of `useManifest`: what a static manifest file holds, and how
 * its entries are merged with the curated preset API's rows. No React and no
 * browser globals, so `scripts/check-preset-library.ts` can test it directly.
 */

/** The shape every library manifest entry shares. */
export interface ManifestEntry {
  id: string;
  label: string;
  /**
   * Where the asset came from. Static manifests record it as `source`; a
   * mirrored preset carries the same URL as `values.sourceUrl`, which
   * `useManifest` spreads onto the item. It is the de-dupe key — see below.
   */
  source?: string;
  sourceUrl?: string;
  /** A bare path (static manifests) or an absolute URL (API presets). */
  file?: string;
  /**
   * `"viewer"`: `file` is a path on the VIEWER's origin rather than beside the
   * manifest. iJewel's material files and textures live there because their
   * CDN sends no CORS header, so only the viewer's own copy reaches WebGL.
   * `thumb` is always relative to the manifest's folder — the host shows it.
   */
  origin?: "viewer";
  thumb?: string;
  /**
   * The workspace's OWN preset (API rows only). False for a curated system
   * preset and for every static-manifest entry — nothing a user may delete.
   */
  owned?: boolean;
  /**
   * Which library an entry belongs to, in a manifest that files several in
   * one place: iJewel's materials put all five categories (metal, gem,
   * ceramic, pearl, other) in one file, and each grid reads its own.
   */
  category?: string;
}

/**
 * The entries of a static manifest file. Two shapes are in use: a bare array
 * (the original libraries) and `{ items: [...] }` (generated manifests, which
 * carry their provenance alongside). Anything else is an empty library.
 */
export function manifestEntries<T extends ManifestEntry = ManifestEntry>(json: unknown): T[] {
  if (Array.isArray(json)) return json as T[];
  const items = (json as { items?: unknown } | null)?.items;
  return Array.isArray(items) ? (items as T[]) : [];
}

const norm = (v?: string) => (v ?? "").trim().toLowerCase();

/** "materials/ijewel/files/metal-gold-hammer.pmat" → "metal-gold-hammer". */
const fileStem = (file?: string) => norm(file?.split(/[\\/]/).pop()?.replace(/\.[^.]+$/, ""));

/**
 * The union of a static library and the preset API's rows for it — statics
 * first, so a library keeps the order people know and a file that ships with
 * the app wins over a database copy of itself.
 *
 * An API row is dropped as a duplicate of a static entry when:
 *
 *   1. PROVENANCE matches — the static `source` and the row's
 *      `values.sourceUrl` are the same CDN URL. This is the exact key, and the
 *      one that matters most: production's library holds an earlier import of
 *      iJewel's materials, named after their CDN files ("metal-whitegold-hammer")
 *      where the static manifest says "White gold hammer". Without it every
 *      material would show twice, and the copy that loses — the database one —
 *      is the one whose textures sit on a host WebGL cannot read.
 *   2. Its NAME is a static entry's label, or the stem of a static entry's
 *      file. A label-only rule missed the gem environments (static "Gem 001",
 *      mirrored "env-gem-001"); the stem catches a mirrored copy even if its
 *      `sourceUrl` was never recorded.
 *
 * The name rule never drops the workspace's OWN preset: a material someone
 * added and happened to call "Yellow gold polished" is theirs, not a copy, and
 * hiding it would make the "+" tile look broken. Provenance still applies to
 * it — a URL match is the same file whoever saved it.
 */
export function mergeLibrary<T extends ManifestEntry>(statics: T[], api: T[]): T[] {
  const origin = (e: ManifestEntry) => norm(e.source ?? e.sourceUrl);
  const sources = new Set(statics.map(origin).filter(Boolean));
  const names = new Set([...statics.map((e) => norm(e.label)), ...statics.map((e) => fileStem(e.file))].filter(Boolean));
  return [
    ...statics,
    ...api.filter((e) => {
      const o = origin(e);
      if (o && sources.has(o)) return false;
      return e.owned === true || !names.has(norm(e.label));
    }),
  ];
}
